import * as Sentry from "@sentry/react";

/**
 * Errors to ignore — browser extension noise, expected auth flows, transient network issues.
 * These would create alert fatigue without providing actionable signal.
 */
const IGNORED_ERRORS = [
  // Browser extensions
  /extensions\//i,
  /^chrome-extension:\/\//,
  /^moz-extension:\/\//,
  // Auth session expiry (expected flow)
  /Auth session missing/i,
  /JWT expired/i,
  /refresh_token_not_found/i,
  // Network transient errors
  /Failed to fetch/i,
  /NetworkError/i,
  /Load failed/i,
  /AbortError/i,
  // ResizeObserver (benign browser quirk)
  /ResizeObserver loop/i,
];

export function initSentry() {
  const dsn = import.meta.env.VITE_SENTRY_DSN;

  if (!dsn) {
    console.log("[Sentry] No DSN configured — skipping init");
    return;
  }

  const isProd = import.meta.env.PROD;

  Sentry.init({
    dsn,
    environment: isProd ? "production" : "development",
    release: import.meta.env.VITE_APP_VERSION || undefined,

    // Performance: 20% in prod, 100% in dev
    tracesSampleRate: isProd ? 0.2 : 1.0,

    // Don't send PII by default
    sendDefaultPii: false,

    // Only enable in production (or when DSN is set in dev for testing)
    enabled: true,

    beforeSend(event) {
      const message = event.exception?.values?.[0]?.value || "";

      // Filter out noise
      for (const pattern of IGNORED_ERRORS) {
        if (pattern.test(message)) {
          return null;
        }
      }

      // Strip local file paths from stack traces
      if (event.exception?.values) {
        for (const exception of event.exception.values) {
          if (exception.stacktrace?.frames) {
            for (const frame of exception.stacktrace.frames) {
              if (frame.filename && !frame.filename.startsWith("http")) {
                frame.filename = frame.filename.replace(/^.*\/src\//, "src/");
              }
            }
          }
        }
      }

      return event;
    },

    // Integrations
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        // Only capture replays on errors, not all sessions
        maskAllText: true,
        blockAllMedia: true,
      }),
    ],

    // Session Replay: 0% baseline, 100% on error
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: isProd ? 1.0 : 0,
  });
}

export { Sentry };
